import { Card, Grid, Typography } from "@mui/material";
import { Methods } from "api";
import { TYPE_ERROR } from "common/constants/common";
import * as MESSAGES from "common/constants/messages";
import { CLAIM_STATUS } from "common/constants/options";
import AssessmentCard from "components/detailsPage/AssessmentCard";
import ContactCard from "components/detailsPage/ContactCard";
import HeaderCard from "components/detailsPage/HeaderCard";
import HistoryCard from "components/detailsPage/HistoryCard";
import LoadingCircle from "components/loadingCircle";
import { useAPI, usePopups } from "hooks";
import { type FC, memo, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { Claim } from "s12-library/types";

interface DuplicatedDetailsProps {
  goBack?: () => void;
  rowId: string;
}

const DuplicatedDetails: FC<DuplicatedDetailsProps> = ({ goBack, rowId }) => {
  const { handleBannerMessage } = usePopups();
  const [data, setData] = useState<Claim | undefined>(undefined);
  const navigate = useNavigate();
  const location = useLocation();

  const {
    data: duplicatedClaimDetailsData,
    loading: duplicatedClaimDetailsLoading,
    error: duplicatedClaimDetailsError,
  } = useAPI<Claim, { id: string }>({
    method: Methods.GET,
    fieldName: "getClaim",
    args: { id: rowId },
  });

  useEffect(() => {
    if (duplicatedClaimDetailsData?.status === CLAIM_STATUS.duplicated) {
      setData(duplicatedClaimDetailsData);
    }
  }, [duplicatedClaimDetailsData]);

  useEffect(() => {
    if (duplicatedClaimDetailsError) {
      handleBannerMessage(TYPE_ERROR, MESSAGES.UNEXPECTED_ERROR_MESSAGE);
    }
  }, [duplicatedClaimDetailsError, handleBannerMessage]);

  useEffect(() => {
    if (location?.state?.isSameMenu && !!goBack) {
      goBack();
      navigate(location.pathname, {
        state: { isSameMenu: false, isInDetailsPage: false },
      });
    }
  }, [goBack, location.pathname, location?.state?.isSameMenu, navigate]);

  return (
    <>
      {duplicatedClaimDetailsLoading ? (
        <LoadingCircle />
      ) : data ? (
        <>
          <HeaderCard
            status={data.status === CLAIM_STATUS.duplicated ? "DUPLICATED" : ""}
            assigneeName={data.assigneeName ?? ""}
            goBack={goBack}
            buttons={[]}
            onClick={() => {}}
            claimId={data.displayId}
          />
          <AssessmentCard claim={data} />
          <ContactCard docData={data.doctor} amhpData={data.amhp} />
          <HistoryCard data={data} />
        </>
      ) : (
        <Grid
          m={1}
          p={2}
          component={Card}
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            borderRadius: 1,
            minHeight: window.innerHeight - 85,
          }}>
          <Grid container m={0}>
            <Grid container spacing={3} sx={{ height: "fit-content" }}>
              <Grid item xs={12}>
                <Typography variant="h1">Unexpected error occurred when fetching the claim data</Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default memo(DuplicatedDetails);
